<template>
  <div class="video-player-wrapper overflow-hidden bg-black h-full" ref="container">
    <!-- Audio mode -->
    <div v-if="isAudio" class="flex flex-col items-center justify-center h-full relative">
      <!-- Equalizer bars -->
      <div class="flex items-end gap-[3px] h-24 mb-6">
        <div v-for="i in 16" :key="i"
          :class="['eq-bar rounded-full', playing ? 'eq-animate' : 'eq-idle']"
          :style="{ animationDelay: (i * 0.07) + 's', height: playing ? '' : '8px' }"
        />
      </div>

      <!-- Track info -->
      <div class="text-center mb-6">
        <p class="text-sm font-medium text-gray-300">{{ playing ? 'Now Playing' : 'Audio Track' }}</p>
      </div>

      <!-- Audio controls -->
      <div class="flex items-center gap-4 w-full max-w-sm px-6">
        <button @click="togglePlay" class="w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors shrink-0">
          <svg v-if="playing" class="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>
          <svg v-else class="w-5 h-5 text-white ml-0.5" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
        </button>
        <div class="flex-1">
          <input
            ref="audioSeek"
            type="range"
            min="0"
            :max="audioDuration || 100"
            :value="audioCurrentTime"
            @input="seekAudio"
            class="audio-seek w-full"
          />
          <div class="flex justify-between mt-1">
            <span class="text-[11px] font-data text-gray-500">{{ formatTime(audioCurrentTime) }}</span>
            <span class="text-[11px] font-data text-gray-500">{{ formatTime(audioDuration) }}</span>
          </div>
        </div>
      </div>

      <!-- Hidden audio element -->
      <audio
        ref="video"
        :src="src"
        @play="playing = true; $emit('playing')"
        @pause="playing = false; $emit('paused')"
        @ended="playing = false; $emit('paused')"
        @canplay="onCanPlay"
        @timeupdate="onTimeUpdate"
        @loadedmetadata="onAudioMeta"
        @error="onError"
      />
    </div>

    <!-- Video mode -->
    <template v-else>
      <video
        ref="video"
        class="w-full h-full"
        :controls="!live"
        playsinline
        @error="onError"
        @play="playing = true; $emit('playing')"
        @pause="playing = false; $emit('paused')"
        @ended="playing = false; $emit('paused')"
        @canplay="onCanPlay"
      />
      <!-- Live: custom controls -->
      <div v-if="live && !error" class="absolute bottom-0 left-0 right-0 vp-controls">
        <div class="flex items-center gap-3 px-4 py-2.5 bg-gradient-to-t from-black/70 to-transparent">
          <button @click="togglePlay" class="p-1 text-white/90 hover:text-white transition-colors">
            <svg v-if="playing" class="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>
            <svg v-else class="w-5 h-5 ml-0.5" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
          </button>
          <div class="flex items-center gap-2">
            <button @click="toggleMute" class="p-1 text-white/80 hover:text-white transition-colors">
              <svg v-if="muted" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>
              <svg v-else class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 010 14.14M15.54 8.46a5 5 0 010 7.07"/></svg>
            </button>
            <input type="range" min="0" max="1" step="0.01" :value="liveVolume" @input="setVolume" class="live-volume" />
          </div>
          <div class="ml-auto flex items-center gap-1.5 px-2 py-0.5 rounded bg-red-600/90">
            <span class="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
            <span class="text-[10px] font-bold text-white uppercase tracking-wide">Live</span>
          </div>
        </div>
      </div>
      <div v-if="error" class="flex flex-col items-center justify-center py-10 text-center bg-gray-900">
        <svg class="w-8 h-8 text-gray-600 mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
        </svg>
        <p class="text-sm text-gray-500">Failed to load video</p>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'VideoPlayer',
  props: {
    src: { type: String, default: '' },
    type: { type: String, default: '' },
    live: { type: Boolean, default: false }
  },

  emits: ['playing', 'paused'],

  data() {
    return {
      hls: null,
      error: false,
      playing: false,
      autoplayPending: false,
      audioCurrentTime: 0,
      audioDuration: 0,
      muted: false,
      liveVolume: 0.5
    }
  },

  computed: {
    isAudio() {
      const t = this.type || this.detectType(this.src)
      return t === 'audio/mpeg' || this.src?.endsWith('.mp3')
    }
  },

  watch: {
    src() {
      this.autoplayPending = true
      this.playing = false
      this.audioCurrentTime = 0
      this.audioDuration = 0
      this.$nextTick(() => this.loadSource())
    }
  },

  mounted() {
    if (this.src) {
      this.autoplayPending = true
      this.$nextTick(() => this.loadSource())
    }
  },

  beforeUnmount() {
    this.destroyHls()
  },

  methods: {
    async loadSource() {
      this.error = false
      this.destroyHls()

      if (this.isAudio) return // audio element handles src directly

      const video = this.$refs.video
      if (!video || !this.src) return

      const contentType = this.type || this.detectType(this.src)

      if (contentType === 'application/x-mpegURL' || this.src.endsWith('.m3u8')) {
        await this.loadHls(video, this.src)
      } else {
        video.src = this.src
        video.type = contentType
      }
    },

    async loadHls(video, src) {
      try {
        const { default: Hls } = await import('hls.js')
        if (Hls.isSupported()) {
          const hlsConfig = this.live ? {} : { startPosition: 0, liveSyncDuration: undefined, liveMaxLatencyDuration: undefined }
          this.hls = new Hls(hlsConfig)
          this.hls.loadSource(src)
          this.hls.attachMedia(video)
          this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
            if (this.autoplayPending) {
              this.autoplayPending = false
              video.play().catch(() => {})
            }
          })
          this.hls.on(Hls.Events.ERROR, (event, data) => {
            if (data.fatal) this.error = true
          })
        } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
          video.src = src
        } else {
          this.error = true
        }
      } catch {
        video.src = src
      }
    },

    destroyHls() {
      if (this.hls) {
        this.hls.destroy()
        this.hls = null
      }
    },

    detectType(url) {
      if (!url) return ''
      if (url.endsWith('.m3u8')) return 'application/x-mpegURL'
      if (url.endsWith('.mp4')) return 'video/mp4'
      if (url.endsWith('.mp3')) return 'audio/mpeg'
      return 'video/mp4'
    },

    play() {
      this.$refs.video?.play()
    },

    pause() {
      this.$refs.video?.pause()
    },

    togglePlay() {
      if (this.playing) this.pause()
      else this.play()
    },

    isPlaying() {
      return this.playing
    },

    onCanPlay() {
      if (this.autoplayPending) {
        this.autoplayPending = false
        this.$refs.video?.play()
      }
    },

    onTimeUpdate() {
      this.audioCurrentTime = this.$refs.video?.currentTime || 0
    },

    onAudioMeta() {
      this.audioDuration = this.$refs.video?.duration || 0
      if (this.autoplayPending) {
        this.autoplayPending = false
        this.$refs.video?.play()
      }
    },

    seekAudio(e) {
      if (this.$refs.video) {
        this.$refs.video.currentTime = Number(e.target.value)
      }
    },

    toggleMute() {
      this.muted = !this.muted
      if (this.$refs.video) this.$refs.video.muted = this.muted
    },

    setVolume(e) {
      this.liveVolume = Number(e.target.value)
      if (this.$refs.video) {
        this.$refs.video.volume = this.liveVolume
        this.muted = this.liveVolume === 0
        this.$refs.video.muted = this.muted
      }
    },

    formatTime(seconds) {
      if (!seconds || isNaN(seconds)) return '0:00'
      const m = Math.floor(seconds / 60)
      const s = Math.floor(seconds % 60)
      return `${m}:${s.toString().padStart(2, '0')}`
    },

    onError() {
      this.error = true
    }
  }
}
</script>

<style scoped>
.video-player-wrapper video {
  display: block;
  width: 100%;
  object-fit: contain;
  background: #000;
}

/* Equalizer bars */
.eq-bar {
  width: 4px;
  background: var(--color-primary);
  transition: height 0.15s ease;
}

.eq-idle {
  height: 8px;
  opacity: 0.3;
}

.eq-animate {
  animation: eq-bounce 0.8s ease-in-out infinite alternate;
}

@keyframes eq-bounce {
  0% { height: 8px; opacity: 0.4; }
  25% { height: 32px; opacity: 0.7; }
  50% { height: 56px; opacity: 1; }
  75% { height: 24px; opacity: 0.6; }
  100% { height: 40px; opacity: 0.8; }
}

/* Audio seek slider */
.audio-seek {
  -webkit-appearance: none;
  appearance: none;
  height: 4px;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.15);
  outline: none;
  cursor: pointer;
}

.audio-seek::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: var(--color-primary);
  cursor: pointer;
  border: 2px solid #fff;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.3);
}

.audio-seek::-moz-range-thumb {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: var(--color-primary);
  cursor: pointer;
  border: 2px solid #fff;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.3);
}

/* Live volume slider */
.live-volume {
  -webkit-appearance: none;
  appearance: none;
  width: 60px;
  height: 4px;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.25);
  outline: none;
  cursor: pointer;
}
.live-volume::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #fff;
  cursor: pointer;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.3);
}
.live-volume::-moz-range-thumb {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #fff;
  cursor: pointer;
  border: none;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.3);
}
</style>
